// AWS Cognito configuration for Hosted Pages authentication
export interface CognitoConfig {
  region: string
  userPoolId: string
  clientId: string
  domain: string
  redirectUri: string
  logoutUri: string
  scopes: string[]
}

// Cognito configuration from environment variables
export const cognitoConfig: CognitoConfig = {
  region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  domain: import.meta.env.VITE_COGNITO_DOMAIN || '', // e.g., 'your-domain.auth.us-east-1.amazoncognito.com'
  redirectUri: import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/auth/callback`,
  logoutUri: import.meta.env.VITE_COGNITO_LOGOUT_URI || `${window.location.origin}/auth/logout`,
  scopes: ['openid']
}

// Validate Cognito configuration
export const validateCognitoConfig = (): boolean => {
  const required = ['userPoolId', 'clientId', 'domain']
  const missing = required.filter(key => !cognitoConfig[key as keyof CognitoConfig])
  
  if (missing.length > 0) {
    console.error('Missing Cognito configuration:', missing)
    return false
  }
  
  return true
}

// Generate PKCE code verifier and challenge
export const generatePKCE = (): { codeVerifier: string; codeChallenge: string } => {
  // Generate code verifier (43-128 characters, URL-safe)
  const codeVerifier = generateRandomString(128)
  
  // Generate code challenge (SHA256 hash of verifier, base64url encoded)
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  
  return crypto.subtle.digest('SHA-256', data).then(hash => {
    const codeChallenge = base64UrlEncode(new Uint8Array(hash))
    return { codeVerifier, codeChallenge }
  }) as any // Temporary type assertion for async
}

// Generate PKCE synchronously for immediate use
export const generatePKCESync = (): { codeVerifier: string; codeChallenge: string } => {
  const codeVerifier = generateRandomString(128)
  // For simplicity, we'll use a simpler challenge method
  // In production, you might want to use the async version above
  const codeChallenge = btoa(codeVerifier)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
  
  return { codeVerifier, codeChallenge }
}

// Generate random string for PKCE
const generateRandomString = (length: number): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length))
  }
  return result
}

// Base64 URL encode
const base64UrlEncode = (buffer: Uint8Array): string => {
  const base64 = btoa(String.fromCharCode(...buffer))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

// Generate state parameter for CSRF protection
export const generateState = (): string => {
  return generateRandomString(32)
}

// Cognito URLs
export const getCognitoUrls = () => {
  const baseUrl = `https://${cognitoConfig.domain}`
  
  return {
    authorize: `${baseUrl}/login`,
    token: `${baseUrl}/oauth2/token`,
    logout: `${baseUrl}/oauth2/logout`,
    userInfo: `${baseUrl}/oauth2/userInfo`
  }
}

// Build authorization URL for Cognito Hosted Pages
export const buildAuthorizationUrl = (registry: string = 'sor'): string => {
  const state = generateState()
  const timestamp = Date.now()
  
  // Store state in both sessionStorage and localStorage for redundancy
  const authData = { state, registry, timestamp }
  sessionStorage.setItem('auth_state', state)
  sessionStorage.setItem('auth_registry', registry)
  localStorage.setItem('auth_temp', JSON.stringify(authData))
  
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoConfig.clientId,
    redirect_uri: cognitoConfig.redirectUri,
    scope: cognitoConfig.scopes.join(' '),
    state: state
  })
  
  return `${getCognitoUrls().authorize}?${params.toString()}`
}

// Build logout URL
export const buildLogoutUrl = (): string => {
  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    logout_uri: cognitoConfig.logoutUri
  })
  
  return `${getCognitoUrls().logout}?${params.toString()}`
}

// Token exchange payload
export interface TokenExchangePayload {
  grant_type: 'authorization_code'
  client_id: string
  code: string
  redirect_uri: string
}

// Token response from Cognito
export interface TokenResponse {
  access_token: string
  id_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

// User info from Cognito
export interface CognitoUser {
  sub: string
  email: string
  email_verified: boolean
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  username: string
}